"use client";

import { useState, type CSSProperties } from "react";
import { format } from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  DayPicker,
  getDefaultClassNames,
  type ChevronProps,
  type DateRange,
} from "react-day-picker";
import "react-day-picker/style.css";
import { twMerge } from "tailwind-merge";
import { FieldSelectButton } from "./FieldSelectButton";

const rdp = getDefaultClassNames();

export interface DateRangeValue {
  from?: Date;
  to?: Date;
}

interface DateRangeInputProps {
  value: DateRangeValue;
  onChange: (value: DateRangeValue) => void;
  className?: string;
}

type RangeField = "from" | "to";

function formatPillDate(date?: Date): string {
  return date ? format(date, "MMM d, yyyy") : "—";
}

function CalendarChevron({ orientation, className }: ChevronProps) {
  const Icon = orientation === "left" ? ChevronLeft : ChevronRight;
  return <Icon className={twMerge("size-3", className, "fill-none!")} />;
}

const CALENDAR_STYLE = {
  "--rdp-accent-color": "var(--brand-blue)",
  "--rdp-accent-background-color": "var(--action-wash)",

  "--rdp-day-width": "35px",
  "--rdp-day-height": "26px",

  "--rdp-day_button-width": "26px",
  "--rdp-day_button-height": "26px",

  "--rdp-nav-height": "26px",
  "--rdp-selected-border": "none",
  "--rdp-outside-opacity": "0.4",
  "--rdp-weekday-opacity": "1",
  "--rdp-weekday-padding": "2px 0",
} as CSSProperties;

export function DateRangeInput({
  value,
  onChange,
  className,
}: DateRangeInputProps) {
  const [activeField, setActiveField] = useState<RangeField>("from");

  const selected: DateRange = { from: value.from, to: value.to };

  const handleSelect = (_range: DateRange | undefined, triggerDate: Date) => {
    // The active header field decides which end of the range the click
    // updates, keeping the other end untouched — unless the click would
    // make the range invalid, in which case the other end is cleared. When
    // that leaves the range incomplete, the active field auto-advances to
    // the now-missing end — but a manual pill click always overrides that.
    if (activeField === "from") {
      const to = value.to && triggerDate <= value.to ? value.to : undefined;
      onChange({ from: triggerDate, to });
      if (!to) setActiveField("to");
      return;
    }
    const from =
      value.from && triggerDate >= value.from ? value.from : undefined;
    onChange({ from, to: triggerDate });
    if (!from) setActiveField("from");
  };

  const handleFieldClick = (field: RangeField) => {
    setActiveField(field);
  };

  const monthChangeButtonClassName =
    "text-[#4B5563]! hover:text-[#1884F9]! transition-colors!";

  return (
    <div className={twMerge("flex flex-col gap-3.5", className)}>
      <div className="bg-brand-gray relative flex overflow-hidden rounded-md">
        <div
          className={twMerge(
            "absolute inset-y-[3px] left-[3px] w-[calc(50%-6px)] rounded-md bg-white transition-transform duration-200 ease-out",
            activeField === "to" && "translate-x-[calc(100%+6px)]",
          )}
        />
        <FieldSelectButton
          label="From"
          value={formatPillDate(value.from)}
          isActive={activeField === "from"}
          onClick={() => handleFieldClick("from")}
        />
        <FieldSelectButton
          label="To"
          value={formatPillDate(value.to)}
          isActive={activeField === "to"}
          onClick={() => handleFieldClick("to")}
        />
      </div>
      <DayPicker
        mode="range"
        navLayout="around"
        min={1}
        defaultMonth={value.from ?? new Date()}
        selected={selected}
        onSelect={handleSelect}
        style={CALENDAR_STYLE}
        components={{ Chevron: CalendarChevron }}
        classNames={{
          caption_label: twMerge(rdp.caption_label, "text-xs"),
          month_grid: twMerge(rdp.month_grid, "mt-2"),
          month_caption: twMerge(rdp.month_caption, "flex items-center"),
          button_previous: twMerge(
            rdp.button_previous,
            monthChangeButtonClassName,
          ),
          button_next: twMerge(rdp.button_next, monthChangeButtonClassName),
          weekday: twMerge(
            rdp.weekday,
            "text-[10px]! font-bold! text-[#B0B4BC]!",
          ),

          range_middle: twMerge(
            rdp.range_middle,
            "text-brand-blue! [&>button]:bg-transparent! [&>button]:text-[#1884F9]!",
          ),
          selected: twMerge(
            rdp.selected,
            "[&>button]:bg-[#1884F9]! text-white!",
          ),
          day: twMerge(
            rdp.day,
            "border-b-[4px] border-white",
            "[&:not(.rdp-selected)>button:hover]:bg-action-wash!",
            "[&:not(.rdp-selected)>button:hover]:text-[#1884F9]!",
          ),
          day_button: twMerge(
            rdp.day_button,
            "mx-auto! text-xs! font-medium! transition-colors",
          ),
        }}
      />
    </div>
  );
}
